// Inventory — API docs §13.
//
// Scope rules (same as everywhere else in the panel):
//   BRANCH — sees only its own stock; never sends `partner` in the body.
//   CHAIN  — sees all branches; narrows reads with `?partner=<branchId>`,
//            and sends `partner` in adjustment bodies (§13.6).
//
// The catalogue (items / categories / units) is read-only for partners.
// Only adjustments (stock corrections) are writable, and only by partners
// acting as management — employees cannot submit adjustments.
import api from "../lib/apiClient";

/* --- catalogue (read-only) -------------------------------------------- */

// §13.1a — sorted list of categories. filter: status, search.
export const listInventoryCategories = (params = {}) =>
  api.get("/inventory/categories", { params }).then((r) => r.data);

// §13.1b — unit table: code, label, dimension, decimals.
export const listInventoryUnits = () =>
  api.get("/inventory/units").then((r) => r.data);

// §13.2 — all active items with partner preferences merged in.
// Includes displayName, unit, minStock, primaryImage.
// Query: page, limit, category, search.
export const listInventoryItems = (params = {}) =>
  api.get("/inventory/items", { params }).then((r) => r.data);

// §13.3 — set chain-level display preferences for one item (CHAIN only).
// Body: displayName (string|null), unit (enum|null), minStock (number|null),
//       sortOrder (number).
// 409 if unit is changed and any outlet already holds stock of the item.
export const updateInventoryItemPreferences = (id, payload) =>
  api.patch(`/inventory/items/${id}`, payload).then((r) => r.data);

/* --- stock ---------------------------------------------------------------- */

// §13.4 — current stock across the org (or one branch).
// Query: page, limit, partner, category, item, employee, search,
//        unrecorded (bool), lowStock (bool), from, to, sort (name|quantity).
export const listStock = (params = {}) =>
  api.get("/inventory", { params }).then((r) => r.data);

// §13.5a — branch and category rollup (counts items, not summed quantities).
// Query: partner, category.
export const getInventorySummary = (params = {}) =>
  api.get("/inventory/summary", { params }).then((r) => r.data);

/* --- entries (submissions) ----------------------------------------------- */

// §13.5b — paginated entries with itemImages, billImages, lines[], billAmount.
// Query: page, limit, partner, type, employee, date, from, to.
export const listInventoryEntries = (params = {}) =>
  api.get("/inventory/entries", { params }).then((r) => r.data);

// §13.5c — single entry with full lines[].
export const getInventoryEntry = (id) =>
  api.get(`/inventory/entries/${id}`).then((r) => r.data);

// §13.5d — movement ledger (history).
// Query: page, limit, partner, type, employee, date, from, to,
//        item, category, entry.
export const listInventoryHistory = (params = {}) =>
  api.get("/inventory/history", { params }).then((r) => r.data);

// §13.5d (detail) — load the parent entry of a movement to get full line data
// (qty, unit, note per line). No standalone /history/:id endpoint is documented;
// this is the canonical way to retrieve complete movement detail.
export const getInventoryMovement = (entryId) =>
  api.get(`/inventory/entries/${entryId}`).then((r) => r.data);

/* --- adjustments (write) -------------------------------------------------- */

// §13.6 — correct outlet stock. Quantities REPLACE the current balance.
// Send expectedVersion on each line to prevent silent overwrites (409 on
// conflict). Management action only — employees cannot submit adjustments.
// Body: partner* (chain only), lines* (1-200: { item, quantity,
//       expectedVersion?, note? }), itemImages, billImages, adjusts
//       (ObjectId of entry being corrected), note, clientRef.
export const createInventoryAdjustment = (payload) =>
  api.post("/inventory/entries", payload).then((r) => r.data);
